import { useEffect, useState } from "react";
import { toISO } from "./dates";

/**
 * Returns today's date in the user's local timezone as ISO YYYY-MM-DD.
 * If `jakartaIfWIB` is true and the user is already in WIB (UTC+7),
 * falls back to local naturally; otherwise still uses local time.
 * Per spec: WIB users see WIB (their local), others see their own local.
 */
export function getTodayISO(): string {
  const now = new Date();
  return toISO(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Returns today ISO computed in Asia/Jakarta timezone explicitly. */
export function getJakartaTodayISO(): string {
  // en-CA -> YYYY-MM-DD format
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

/**
 * Reactive hook that returns today's ISO date and re-renders when the day
 * rolls over. Avoids SSR mismatch by returning `null` on first render and
 * the real value after mount.
 */
export function useToday(): string | null {
  const [iso, setIso] = useState<string | null>(null);
  useEffect(() => {
    const compute = () => setIso(getTodayISO());
    compute();
    // Check every minute for day rollover
    const id = window.setInterval(compute, 60_000);
    return () => window.clearInterval(id);
  }, []);
  return iso;
}

/** Add days to ISO without timezone drift. */
export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return toISO(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}